import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { libraryShape, userIdentityShape } from '@bibliocommons/bc-prop-types';
import { eventShape } from 'app/shapes/events';
import ModalActions from 'app/actions/ModalActions';
import EventsActions from 'app/actions/EventsActions';
import { isConflictError } from 'app/helpers/events/errorHelpers';
import RegistrationForm from './RegistrationForm';
import ExistingRegistrationPrompt from './ExistingRegistrationPrompt';

import './EventRegistrationModal.scss';

const CONTACT_PREFERENCES = {
  EMAIL: 'email'
};

export default function EventRegistrationModal({
  currentLibrary,
  currentUser,
  event,
  eventSeries,
  modalKey,
  status,
  eventSettings
}) {
  const dispatch = useDispatch();
  const [firstName, setFirstName] = useState(currentUser?.getIn(['userContact', 'firstName']) ?? '');
  const [lastName, setLastName] = useState(currentUser?.getIn(['userContact', 'lastName']) ?? '');
  const [contactValue, setContactValue] = useState(currentUser?.getIn(['userContact', 'email']) ?? '');
  const [contactType, setContactType] = useState(CONTACT_PREFERENCES.EMAIL);
  const [numberAttending, setNumberAttending] = useState(1);
  const [conflictError, setConflictError] = useState(null);

  function handleClose() {
    setConflictError(null);
    dispatch(ModalActions.closeModal(modalKey));
  }

  function handleConflict(err) {
    setConflictError(err);
  }

  function submitRegistration({ allowDups = false } = {}) {
    const query = {
      eventId: event.get('id'),
      seriesId: event.get('seriesId'),
      userFirstName: firstName,
      userLastName: lastName,
      contactType,
      contactValue,
      numberAttending,
      scope: event.getIn(['definition', 'registrationInfo', 'scope']),
      status,
      ...(allowDups && { allowDups: true })
    };
    dispatch(
      EventsActions.createRegistration({
        query,
        postSuccess: () => handleClose(),
        postFailure: err => {
          if (!allowDups && isConflictError(err)) {
            handleConflict(err);
          } else {
            handleClose();
          }
        }
      })
    );
  }

  function handleCompleteRegistration() {
    submitRegistration({ allowDups: true });
  }

  if (conflictError) {
    return (
      <div className="cp-event-registration-modal">
        <ExistingRegistrationPrompt
          contactValue={contactValue}
          event={event}
          eventSeries={eventSeries}
          status={status}
          onClose={handleClose}
          onCompleteRegistration={handleCompleteRegistration}
        />
      </div>
    );
  }

  return (
    <div className="cp-event-registration-modal">
      <RegistrationForm
        currentLibrary={currentLibrary}
        currentUser={currentUser}
        event={event}
        eventSeries={eventSeries}
        status={status}
        eventSettings={eventSettings}
        firstName={firstName}
        lastName={lastName}
        contactValue={contactValue}
        contactType={contactType}
        onFirstNameChange={setFirstName}
        onLastNameChange={setLastName}
        onContactValueChange={setContactValue}
        onContactTypeChange={setContactType}
        numberAttending={numberAttending}
        onNumberAttendingChange={val => setNumberAttending(Number(val))}
        onClose={handleClose}
        onSubmit={submitRegistration}
      />
    </div>
  );
}

EventRegistrationModal.propTypes = {
  currentLibrary: libraryShape.isRequired,
  currentUser: userIdentityShape,
  event: eventShape.isRequired,
  eventSeries: ImmutablePropTypes.map.isRequired,
  modalKey: PropTypes.string.isRequired,
  status: PropTypes.oneOf(['open', 'waitlist', 'closed']),
  eventSettings: ImmutablePropTypes.mapContains({
    googleMapsOptions: ImmutablePropTypes.mapContains({
      apiKey: PropTypes.string.isRequired
    }),
    eventRegistrationOptions: ImmutablePropTypes.mapContains({
      providerExternal: PropTypes.bool.isRequired,
      providerBiblioEvents: PropTypes.bool.isRequired,
      maxSeats: PropTypes.number,
      emailRequiredForWaitlist: PropTypes.bool.isRequired
    }),
    registrationContactSettings: ImmutablePropTypes.mapContains({
      loggedInPhoneEnabled: PropTypes.bool,
      registrationPhoneEnabled: PropTypes.bool,
      waitlistPhoneEnabled: PropTypes.bool
    })
  })
};
